/**
 * Mark Email as Not in Context Modal
 * Confirmation modal for marking an email as not relevant to logistics context
 */
import { useTranslation } from "react-i18next";
import { Button } from "@/components/base/buttons/button";
import { ModalOverlay, Modal, Dialog } from "@/components/application/modals/modal";
import { X, XClose, AlertTriangle } from "@untitledui/icons";

interface MarkNotInContextModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    isLoading?: boolean;
}

export const MarkNotInContextModal = ({
    isOpen,
    onClose,
    onConfirm,
    isLoading = false,
}: MarkNotInContextModalProps) => {
    const { t } = useTranslation(["pages:activity"]);

    if (!isOpen) return null;

    return (
        <ModalOverlay isOpen={isOpen} onOpenChange={(open) => !open && onClose()}>
            <Modal className="max-w-md">
                <Dialog>
                    <div className="w-full rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                        {/* Header */}
                        <div className="mb-6 flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-primary">
                                {t("emailActions.markNotInContext.confirmTitle", { defaultValue: "Rimuovi Email dal Contesto" })}
                            </h2>
                            <Button
                                onClick={onClose}
                                color="tertiary"
                                size="sm"
                                iconLeading={X}
                                aria-label="Close"
                            />
                        </div>

                        {/* Warning Icon */}
                        <div className="mb-4 flex justify-center">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-warning-secondary">
                                <AlertTriangle className="h-6 w-6 text-warning-primary" />
                            </div>
                        </div>

                        {/* Message */}
                        <p className="mb-6 text-center text-sm text-tertiary">
                            {t("emailActions.markNotInContext.confirmMessage", { 
                                defaultValue: "Sei sicuro di voler marcare questa email come non rilevante? L'email verrà rimossa dal contesto logistico e non verrà più processata." 
                            })}
                        </p>

                        {/* Actions */}
                        <div className="flex justify-end gap-3">
                            <Button
                                onClick={onClose}
                                isDisabled={isLoading}
                                color="secondary"
                                size="sm"
                            >
                                {t("emailActions.markNotInContext.cancel", { defaultValue: "Annulla" })}
                            </Button>
                            <Button
                                onClick={onConfirm}
                                isDisabled={isLoading}
                                isLoading={isLoading}
                                color="secondary-destructive"
                                size="sm"
                                iconLeading={!isLoading ? XClose : undefined}
                            >
                                {isLoading 
                                    ? t("emailActions.markNotInContext.marking", { defaultValue: "Rimozione..." }) 
                                    : t("emailActions.markNotInContext.confirm", { defaultValue: "Rimuovi dal Contesto" })
                                }
                            </Button>
                        </div>
                    </div>
                </Dialog>
            </Modal>
        </ModalOverlay>
    );
};
